import { useEffect, useRef, useCallback } from 'react';

/**
 * Custom hook to trap focus within a container element.
 * Essential for accessibility in modals, mobile menus, and dialogs.
 * 
 * @param isActive - Whether the focus trap is currently active
 * @returns ref to attach to the container element
 */
export function useFocusTrap<T extends HTMLElement>(isActive: boolean) {
  const containerRef = useRef<T>(null);
  const previousActiveElement = useRef<Element | null>(null);

  const getFocusableElements = useCallback(() => {
    if (!containerRef.current) return [];
    
    const focusableSelectors = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(',');

    return Array.from(
      containerRef.current.querySelectorAll<HTMLElement>(focusableSelectors)
    ).filter((el) => {
      // Filter out hidden elements
      return el.offsetParent !== null && getComputedStyle(el).visibility !== 'hidden';
    });
  }, []);

  // Handle keyboard navigation within the trap
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!isActive || event.key !== 'Tab') return;

      const focusableElements = getFocusableElements();
      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      // Shift + Tab: move backwards
      if (event.shiftKey) {
        if (document.activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
        }
      } else {
        // Tab: move forwards
        if (document.activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      }
    },
    [isActive, getFocusableElements]
  );

  // Handle Escape key to close
  const handleEscape = useCallback(
    (event: KeyboardEvent) => {
      if (isActive && event.key === 'Escape') {
        // Dispatch a custom event that parent can listen to
        containerRef.current?.dispatchEvent(
          new CustomEvent('focustrap:escape', { bubbles: true })
        );
      }
    },
    [isActive]
  );

  useEffect(() => {
    if (isActive) {
      // Store the previously focused element
      previousActiveElement.current = document.activeElement;

      // Focus the first focusable element in the container
      const focusableElements = getFocusableElements();
      if (focusableElements.length > 0) {
        // Small delay to ensure the container is fully rendered
        requestAnimationFrame(() => {
          focusableElements[0].focus();
        });
      }

      // Add event listeners
      document.addEventListener('keydown', handleKeyDown);
      document.addEventListener('keydown', handleEscape);

      // Prevent scrolling on body when trap is active
      document.body.style.overflow = 'hidden';
    } else {
      // Restore focus to previously focused element
      if (previousActiveElement.current instanceof HTMLElement) {
        previousActiveElement.current.focus();
      }

      // Re-enable scrolling
      document.body.style.overflow = '';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isActive, handleKeyDown, handleEscape, getFocusableElements]);

  return containerRef;
}

export default useFocusTrap;
